/**
 * ROLE MAP — the seam between the brand and the product.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS THE ONLY FILE WHERE BRAND COLOUR NAMES APPEAR.
 *
 * "Twilight", "Parchment", "Charcoal" are the 2026-08 identity. The next
 * identity will call them something else. Everything downstream of this file —
 * `semanticTokens.ts`, every component, every test — references the ROLE
 * (`accent`, `neutral`, `success`) and never the brand name, so a rebrand is an
 * edit to the right-hand column here rather than a repo-wide find-and-replace.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This is the middle tier of the standard three-tier token model — reference →
 * system → component — as used by Material 3, Radix Colors and the W3C Design
 * Tokens spec:
 *
 *   1. REFERENCE  colors.brand.ts   raw ramps, brand-named, volatile
 *   2. ROLE       this file          what each ramp is FOR, stable
 *   3. SYSTEM     semanticTokens.ts  bg.canvas, text.primary, light/dark pairs
 *
 * A role is a JOB, not a colour. `accent` is "the identity colour used for
 * action"; it is currently blue and has previously been purple. `danger` is
 * "the destructive colour"; it is currently Poppy and was previously a generic
 * red. Neither name has to change again.
 *
 * Enforced by `__tests__/tokenHygiene.test.ts`, which fails if any semantic
 * token references a palette key that is not a role.
 */

import { brandColors } from './colors.brand';

export const ROLES = {
  /**
   * The identity colour. Actions, links, focus, selection.
   * 2026-08: Twilight. Note the AA split documented in colors.brand.ts —
   * `accent.500` is the identity value but cannot carry body-size text.
   */
  accent: brandColors.twilight,

  /**
   * Ink and chrome: body text, borders, hover/active tints, dark-mode surfaces.
   * 2026-08: the tuned Charcoal/Parchment ladder. Two of its steps (350, 500)
   * exist only to clear WCAG 1.4.11 on form-field borders and pressed rows —
   * see colors.brand.ts before "simplifying" them.
   */
  neutral: brandColors.sand,

  /** Page and card grounds. 2026-08: Parchment (six steps, 100-600). */
  surface: brandColors.parchment,

  /** Positive / confirmed. 2026-08: Emerald. */
  success: brandColors.emerald,

  /** Destructive / failed. 2026-08: Poppy. */
  danger: brandColors.poppy,

  /** Caution / needs attention. 2026-08: Sunset. */
  warning: brandColors.sunset,

  /** Neutral information, and the logo mark's gradient partner. 2026-08: Bluebird. */
  info: brandColors.bluebird,

  /**
   * Non-status decorative tints — entity icons, tags, avatar hashing. These
   * need to be visually DISTINCT from each other rather than to mean anything,
   * which is why they are a set rather than a single ramp.
   */
  decorativeWarm: brandColors.sandstone,
  decorativeDeep: brandColors.oxblood,
} as const;

/**
 * The palette keys a semantic token is allowed to reference. Chakra's own
 * alpha scales are permitted because they are transparency, not colour.
 *
 * @internal Test seam: tokenHygiene.test checks every semantic token against it.
 */
export const ALLOWED_PALETTES: ReadonlyArray<string> = [
  ...Object.keys(ROLES),
  // Named single-purpose surfaces from colors.ts — not ramps, but legitimate
  // targets for a semantic token.
  'ui',
  'ink',
  'glow',
  'whiteAlpha',
  'blackAlpha',
  'white',
  'black',
];
